import { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import { cn } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Search } from 'lucide-react';
import RunningExerciseCard from './RunningExerciseCard';
import RunningExerciseDetail from './RunningExerciseDetail';

function formatDurationSec(sec) {
  if (sec == null || !Number.isFinite(Number(sec))) return '—';
  const s = Math.round(Number(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  return `${m}:${String(r).padStart(2, '0')}`;
}

export default function SessionPickerModal({
  open,
  onOpenChange,
  onSelect,
  titleKey,
  descriptionKey,
  priority = 'nested',
}) {
  const { t } = useLanguage();
  const [subtypes, setSubtypes] = useState([]);
  const [subtypeFilter, setSubtypeFilter] = useState('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const pageSize = 48;
  const [hasMore, setHasMore] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSessionId, setDetailSessionId] = useState(null);
  const [detailTitle, setDetailTitle] = useState('');

  useEffect(() => {
    const tmr = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(tmr);
  }, [q]);

  useEffect(() => {
    if (!open) return;
    api
      .get('/api/training/running-workout-subtypes')
      .then((res) => setSubtypes(res.data.subtypes || []))
      .catch(() => setSubtypes([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const params = { limit: pageSize, offset: 0 };
    if (debouncedQ) params.q = debouncedQ;
    if (subtypeFilter) params.subtype_key = subtypeFilter;
    api
      .get('/api/training/running-sessions', { params })
      .then((res) => {
        if (cancelled) return;
        const next = res.data.sessions || [];
        setSessions(next);
        setHasMore(next.length >= pageSize);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) {
          setSessions([]);
          setHasMore(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, debouncedQ, subtypeFilter, pageSize]);

  const loadMore = () => {
    setLoading(true);
    const params = { limit: pageSize, offset: sessions.length };
    if (debouncedQ) params.q = debouncedQ;
    if (subtypeFilter) params.subtype_key = subtypeFilter;
    api
      .get('/api/training/running-sessions', { params })
      .then((res) => {
        const next = res.data.sessions || [];
        setSessions((prev) => [...prev, ...next]);
        setHasMore(next.length >= pageSize);
      })
      .catch(() => setHasMore(false))
      .finally(() => setLoading(false));
  };

  const handlePick = (sess) => {
    if (onSelect) onSelect(sess);
    onOpenChange(false);
    setQ('');
    setSubtypeFilter('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        priority={priority}
        className="max-w-[min(90vw,960px)] max-h-[min(90vh,820px)] flex flex-col gap-0 p-0 overflow-hidden"
      >
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-border">
          <DialogTitle>{t(titleKey || 'training.sessionPicker.title')}</DialogTitle>
          <DialogDescription>{t(descriptionKey || 'training.sessionPicker.description')}</DialogDescription>
        </DialogHeader>

        <div className="px-6 py-3 space-y-3 shrink-0 border-b border-border bg-muted/10">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                className="pl-8"
                placeholder={t('training.sessionPicker.searchPlaceholder')}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-[220px] space-y-1">
              <Label className="text-xs text-muted-foreground">{t('training.sessionPicker.subtype')}</Label>
              <Select value={subtypeFilter || '__all'} onValueChange={(v) => setSubtypeFilter(v === '__all' ? '' : v)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={t('training.sessionPicker.allSubtypes')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">{t('training.sessionPicker.allSubtypes')}</SelectItem>
                  {subtypes.map((st) => (
                    <SelectItem key={st.id} value={st.subtype_key}>
                      {st.label_key ? t(st.label_key) : st.subtype_key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          {loading && sessions.length === 0 ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
            </div>
          ) : sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">{t('training.sessionPicker.empty')}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {sessions.map((sess) => (
                <div
                  key={sess.id}
                  className={cn(
                    'rounded-xl border border-border bg-card shadow-sm',
                    'transition-all duration-200 hover:border-primary/40 hover:shadow-md'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => handlePick(sess)}
                    className="w-full text-left rounded-t-xl p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-sm font-semibold text-foreground line-clamp-2 leading-tight">{sess.name}</h3>
                      {sess.weighted_intensity != null && (
                        <Badge variant="secondary" className="shrink-0 text-[10px] tabular-nums">
                          {t('training.sessionPicker.intensity')}{' '}
                          {(Number(sess.weighted_intensity) * 10).toFixed(1)}
                        </Badge>
                      )}
                    </div>
                    {!!sess.description && (
                      <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{sess.description}</p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1">
                      <Badge variant="outline" className="text-[10px] font-normal">
                        {formatDurationSec(sess.estimated_duration_sec)}
                      </Badge>
                      {(sess.subtype_keys || []).slice(0, 4).map((k) => (
                        <Badge key={k} variant="outline" className="text-[10px] font-normal capitalize">
                          {k.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </button>
                  <div className="px-3 pb-3">
                    <RunningExerciseCard
                      className="border-0 shadow-none bg-transparent p-0"
                      title={null}
                      description={null}
                      splits={sess.preview_splits || []}
                      vizHeightPx={40}
                      onOpenDetail={() => {
                        setDetailSessionId(sess.id);
                        setDetailTitle(sess.name || '');
                        setDetailOpen(true);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          {hasMore && sessions.length > 0 && (
            <div className="flex justify-center pt-4 pb-2">
              <Button type="button" variant="outline" size="sm" disabled={loading} onClick={loadMore}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training.sessionPicker.loadMore')}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>

      <RunningExerciseDetail
        open={detailOpen}
        onOpenChange={setDetailOpen}
        mode="library"
        librarySessionId={detailSessionId}
        fallbackTitle={detailTitle}
      />
    </Dialog>
  );
}
